import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

import { uniqBy } from 'lodash-es';
import { Category } from '../router/category';
import { CategoryType } from '../shared/types';
import { VppApplicationsStoreSelectors, VppApplicationsStoreStates } from '../vpp-applications';
import * as fromCategoryVppApplications from './category-vpp-applications.reducer';
import { CategoryVppApplicationsState, VppApplicationConfiguration } from './category-vpp-applications.state';

export interface CategoryVppApplication extends VppApplicationsStoreStates.VppApplication {
  id?: string;
  type: CategoryType;
  applyFrom?: Date;
  version?: string;
}

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryVppApplicationsState =
  createFeatureSelector<CategoryVppApplicationsState>('categoryVppApplications');

export const selectAllCategoryVppApplications = createSelector(
  selectCategoryVppApplicationsState,
  fromCategoryVppApplications.selectAllCategoryVppApplications
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Maps VPP application configurations for a specific category by:
 * 1. Filtering applications by category ID and type
 * 2. Enriching filtered applications with full VPP app details from the store
 * 3. Adding category-specific metadata (id, type, applyFrom, version)
 */
const mapVppApplicationsForCategory = (
  category: Category,
  vppApps: VppApplicationsStoreStates.VppApplication[],
  applications: VppApplicationConfiguration[]
): CategoryVppApplication[] => {
  return (
    applications
      // Filter to only applications for this specific category
      .filter((app) => app.categoryId === category.categoryId && app.type === category.categoryType)
      // Enrich with full VPP application details from the store
      .map((x) => {
        const application = vppApps.find((y) => y.adamIdStr === x.adamIdStr);
        return {
          ...application, // Spread VPP app details (bundleId, name, etc.)
          id: category.categoryId,
          type: category.categoryType,
          applyFrom: x.applyFrom,
          version: x.version,
        };
      })
  );
};

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

export const createSelectVppApplicationsLinkedToCategory = (
  category: Category
): MemoizedSelector<object, CategoryVppApplication[]> => {
  return createSelector(
    VppApplicationsStoreSelectors.selectAllVppApplications,
    selectAllCategoryVppApplications,
    (
      vppApps: VppApplicationsStoreStates.VppApplication[],
      applications: VppApplicationConfiguration[]
    ): CategoryVppApplication[] => mapVppApplicationsForCategory(category, vppApps, applications)
  );
};

export const createSelectVppApplicationsLinkedToCategoryLoaded = (
  category: Category
): MemoizedSelector<object, boolean> => {
  return createSelector(
    createSelectVppApplicationsLinkedToCategory(category),
    (applications) => applications.length > 0
  );
};

export const createSelectDistinctVppApplicationsLinkedToCategory = (
  category: Category
): MemoizedSelector<object, CategoryVppApplication[]> => {
  return createSelector(createSelectVppApplicationsLinkedToCategory(category), (applications) =>
    uniqBy(applications, (app: CategoryVppApplication) => app.adamIdStr)
  );
};

export const createSelectDistinctBundleIdentifiersLinkedToCategory = (
  category: Category
): MemoizedSelector<object, string[]> => {
  return createSelector(createSelectDistinctVppApplicationsLinkedToCategory(category), (applications) =>
    applications.map((application: CategoryVppApplication) => application.bundleId)
  );
};

// ============================================================================
// GENERIC SELECTORS - One-liner selectors for containers
// ============================================================================

/**
 * Generic selector for VPP applications of any category.
 * Automatically handles router selector mapping and inheritance logic.
 *
 * Usage in containers:
 * this.linkedApplications$ = this.store.select(createSelectVppApplicationsLinkedToCategory(category));
 */
export const selectVppApplicationsAppliedToCategory = (
  category: Category
): MemoizedSelector<object, CategoryVppApplication[]> => {
  // HACK: These categories do not support inheritance for applications, so we return the applications linked to the category directly.
  // This is a temporary solution until we have a proper plan for our inheritance logic, see user story #35828.
  if (
    category.categoryType === 'Bar' ||
    category.categoryType === 'FrontDesk' ||
    category.categoryType === 'Housekeeping' ||
    category.categoryType === 'Kiosk' ||
    category.categoryType === 'Staff'
  ) {
    return createSelectVppApplicationsLinkedToCategory(category);
  }

  if (category.parent) {
    return createSelector(
      selectVppApplicationsAppliedToCategory(category.parent),
      createSelectVppApplicationsLinkedToCategory(category),
      (parent, current) => [...parent, ...current]
    );
  } else {
    return createSelectVppApplicationsLinkedToCategory(category);
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Groups VPP applications by BundleIdentifier to show all versions (active and scheduled) in the same element.
 * VPP applications have scheduling logic but cannot specify specific versions.
 * This grouping is purely aesthetic and operations are still applied through the Store in the associated application-link component.
 */
export const getGroupedVppApplications = (
  applications: CategoryVppApplication[]
): (CategoryVppApplication & {
  state: 'active' | 'scheduled' | 'previous';
})[][] => {
  const groupedApplications = applications.reduce<Record<string, CategoryVppApplication[]>>((groups, app) => {
    const key = app.bundleId;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(app);
    return groups;
  }, {});

  const groupedApplicationsArray = Object.values(groupedApplications);
  const filteredGroupedApplications = filterOutInheritedVppApps(groupedApplicationsArray);

  return filteredGroupedApplications.map((group: CategoryVppApplication[]) => {
    return group.map((app: CategoryVppApplication) => {
      const state = getVppApplicationState(app, group);

      const application: CategoryVppApplication & {
        state: 'active' | 'scheduled' | 'previous';
      } = { ...app, state: state };

      return application;
    });
  });
};

/**
 * Determines the state of a VPP application within a group (active, scheduled, or previous).
 */
const getVppApplicationState = (
  application: CategoryVppApplication,
  group: CategoryVppApplication[]
): 'active' | 'scheduled' | 'previous' => {
  const activeApp: CategoryVppApplication = getActiveVppApp(group);

  if (application === activeApp) {
    return 'active';
  } else if (new Date(application.applyFrom) > new Date()) {
    return 'scheduled';
  } else {
    return 'previous';
  }
};

/**
 * When a VPP app has configurations on multiple levels, filter out the configs inherited from higher levels.
 * For example, filter out Location config when a config is present on Room level.
 */
const filterOutInheritedVppApps = (groupedApplications: CategoryVppApplication[][]) => {
  return groupedApplications.map((apps: CategoryVppApplication[]) => {
    // Guard, if there is only one app then there is no need to check for inheritance.
    if (apps.length == 1) {
      return apps;
    }

    // We check whether an active configuration exists within the non-inherited configs. If the active configuration is inherited, it is shown in the UI
    const roomLevelApps = apps.filter((app: CategoryVppApplication) => app.id != null && app.type === 'Room');
    const hasRoomLevelConfiguration = roomLevelApps.length > 0;

    const locationLevelApps = apps.filter((app: CategoryVppApplication) => app.id != null && app.type === 'Location');
    const hasLocationLevelConfiguration = locationLevelApps.length > 0;

    if (hasRoomLevelConfiguration && getActiveVppApp(roomLevelApps)) {
      return roomLevelApps;
    } else if (hasLocationLevelConfiguration && getActiveVppApp(locationLevelApps)) {
      return locationLevelApps;
    } else {
      return apps;
    }
  });
};

/**
 * Gets the active VPP application from a list of versions.
 * The active app is the most recently applied app that isn't scheduled.
 * VPP applications have scheduling logic but cannot specify specific versions.
 */
const getActiveVppApp = (applications: CategoryVppApplication[]) => {
  // The active app is the most recently applied app that isn't scheduled. Don't mutate the existing array to not
  // alter the order
  const sorted = [...applications].sort(
    (a: CategoryVppApplication, b: CategoryVppApplication) =>
      new Date(b.applyFrom).getTime() - new Date(a.applyFrom).getTime()
  );
  const activeApp = sorted.find(
    (app: CategoryVppApplication) => new Date(app.applyFrom).getTime() <= new Date().getTime()
  );

  return activeApp;
};
